import React, { useState, useMemo } from 'react';
import {
  EquipmentUsage,
  MaterialCategory,
  EquipmentCondition,
} from '@models/warehouse.model';
import { Badge } from '@components/atoms/Badge';
import { Loading } from '@components/atoms/Loading';
import { Tab } from '@components/atoms/Tab';
import { EmptyData } from '@components/molecules/EmptyData';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { SingleValue, MultiValue } from 'react-select';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BuildIcon from '@mui/icons-material/Build';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDateOnly } from '@utils/formatDate';

interface EquipmentTransactionHistoryTableProps {
  usageHistory: EquipmentUsage[];
  categories?: MaterialCategory[];
  isLoading?: boolean;
}

type FilterTab = 'all' | 'active' | 'returned';

const ITEMS_PER_PAGE = 10;

const CONDITION_LABELS: Record<EquipmentCondition, string> = {
  [EquipmentCondition.GOOD]: 'Good',
  [EquipmentCondition.FAIR]: 'Fair',
  [EquipmentCondition.NEEDS_REPAIR]: 'Needs Repair',
};

export const EquipmentTransactionHistoryTable: React.FC<EquipmentTransactionHistoryTableProps> = ({
  usageHistory,
  categories = [],
  isLoading = false,
}) => {
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [selectedTaskId, setSelectedTaskId] = useState<string>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Extract unique users, projects, tasks from data
  const userOptions = useMemo(() => {
    const map = new Map<string, string>();
    usageHistory.forEach((u) => {
      if (u.checkoutUser?.userId) map.set(u.checkoutUser.userId, `${u.checkoutUser.firstName} ${u.checkoutUser.lastName}`);
      if (u.returnUser?.userId) map.set(u.returnUser.userId, `${u.returnUser.firstName} ${u.returnUser.lastName}`);
    });
    return Array.from(map, ([value, label]) => ({ value, label }));
  }, [usageHistory]);

  const projectOptions = useMemo(() => {
    const map = new Map<string, string>();
    usageHistory.forEach((u) => {
      if (u.task?.project?.projectId) map.set(u.task.project.projectId, u.task.project.name);
    });
    return Array.from(map, ([value, label]) => ({ value, label }));
  }, [usageHistory]);

  const taskOptions = useMemo(() => {
    const map = new Map<string, string>();
    usageHistory.forEach((u) => {
      if (u.task?.taskId) {
        if (selectedProjectId && u.task?.project?.projectId !== selectedProjectId) return;
        map.set(u.task.taskId, u.task.name);
      }
    });
    return Array.from(map, ([value, label]) => ({ value, label }));
  }, [usageHistory, selectedProjectId]);

  const filteredUsage = useMemo(() => {
    return usageHistory.filter((usage) => {
      if (activeTab === 'active' && usage.returnDate) return false;
      if (activeTab === 'returned' && !usage.returnDate) return false;

      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = usage.asset?.name?.toLowerCase().includes(query);
        const matchesCode = usage.asset?.assetCode?.toLowerCase().includes(query);
        const matchesTask = usage.task?.name?.toLowerCase().includes(query);
        const matchesProject = usage.task?.project?.name?.toLowerCase().includes(query);
        const matchesNotes = usage.notes?.toLowerCase().includes(query);
        const matchesUser = usage.checkoutUser ? `${usage.checkoutUser.firstName} ${usage.checkoutUser.lastName}`.toLowerCase().includes(query) : false;
        if (!matchesName && !matchesCode && !matchesTask && !matchesProject && !matchesNotes && !matchesUser) return false;
      }

      if (selectedCategoryId && usage.asset?.categoryId !== selectedCategoryId) return false;
      if (selectedUserId) {
        const matchesCheckout = usage.checkoutUser?.userId === selectedUserId;
        const matchesReturn = usage.returnUser?.userId === selectedUserId;
        if (!matchesCheckout && !matchesReturn) return false;
      }
      if (selectedProjectId && usage.task?.project?.projectId !== selectedProjectId) return false;
      if (selectedTaskId && usage.task?.taskId !== selectedTaskId) return false;

      if (startDate && usage.checkoutDate && new Date(usage.checkoutDate) < new Date(startDate)) return false;
      if (endDate && usage.checkoutDate) {
        const filterEnd = new Date(endDate);
        filterEnd.setHours(23, 59, 59, 999);
        if (new Date(usage.checkoutDate) > filterEnd) return false;
      }

      return true;
    });
  }, [usageHistory, activeTab, searchQuery, selectedCategoryId, selectedUserId, selectedProjectId, selectedTaskId, startDate, endDate]);

  const sortedUsage = useMemo(() => {
    return [...filteredUsage].sort((a, b) => {
      const dateA = a.checkoutDate ? new Date(a.checkoutDate).getTime() : 0;
      const dateB = b.checkoutDate ? new Date(b.checkoutDate).getTime() : 0;
      return dateB - dateA;
    });
  }, [filteredUsage]);

  const totalPages = Math.ceil(sortedUsage.length / ITEMS_PER_PAGE);
  const paginatedUsage = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedUsage.slice(start, start + ITEMS_PER_PAGE);
  }, [sortedUsage, currentPage]);

  const stats = useMemo(() => ({
    total: usageHistory.length,
    active: usageHistory.filter((u) => !u.returnDate).length,
    returned: usageHistory.filter((u) => !!u.returnDate).length,
  }), [usageHistory]);

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  const getConditionVariant = (condition?: EquipmentCondition): 'success' | 'warning' | 'error' | 'secondary' => {
    switch (condition) {
      case EquipmentCondition.GOOD: return 'success';
      case EquipmentCondition.FAIR: return 'warning';
      case EquipmentCondition.NEEDS_REPAIR: return 'error';
      default: return 'secondary';
    }
  };

  const handlePageChange = (page: number) => { if (page >= 1 && page <= totalPages) setCurrentPage(page); };
  const handleTabChange = (tab: FilterTab) => { setActiveTab(tab); setCurrentPage(1); };
  const handleSearchChange = (value: string) => { setSearchQuery(value); setCurrentPage(1); };

  const hasActiveFilters = searchQuery || selectedCategoryId || selectedUserId || selectedProjectId || selectedTaskId || startDate || endDate;

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategoryId('');
    setSelectedUserId('');
    setSelectedProjectId('');
    setSelectedTaskId('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const handleSelectChange = (setter: (val: string) => void) =>
    (option: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
      if (option && !Array.isArray(option) && 'value' in option) {
        setter(String(option.value));
      } else {
        setter('');
      }
      setCurrentPage(1);
    };

  if (isLoading) return <Loading size="xl" center />;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
          <div className="flex items-center justify-between">
            <div><p className="text-sm text-gray-500">Total</p><p className="text-2xl font-semibold text-gray-900">{stats.total}</p></div>
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center"><BuildIcon className="text-indigo-600" sx={{ fontSize: 20 }} /></div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
          <div className="flex items-center justify-between">
            <div><p className="text-sm text-gray-500">Active (Checked Out)</p><p className="text-2xl font-semibold text-orange-600">{stats.active}</p></div>
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center"><span className="text-orange-600 font-bold">→</span></div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
          <div className="flex items-center justify-between">
            <div><p className="text-sm text-gray-500">Returned</p><p className="text-2xl font-semibold text-green-600">{stats.returned}</p></div>
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center"><span className="text-green-600 font-bold">←</span></div>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4 space-y-4">
        <Tab
          tabs={[
            { id: 'all', name: 'All' },
            { id: 'active', name: 'Active', count: stats.active > 0 ? stats.active : undefined },
            { id: 'returned', name: 'Returned', count: stats.returned > 0 ? stats.returned : undefined },
          ]}
          activeTab={activeTab}
          onTabChange={(tabId) => handleTabChange(tabId as FilterTab)}
          variant="underline"
        />

        {/* Filters Row 1 */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-full sm:w-auto sm:min-w-[200px] sm:max-w-[260px]">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" sx={{ fontSize: 18 }} />
            <input type="text" placeholder="Search equipment, tasks..." value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full border border-gray-300 rounded-md py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
          </div>

          {categories.length > 0 && (
            <div className="w-full sm:w-[170px]">
              <ReactSelect
                options={categories.map((cat) => ({ value: cat.categoryId, label: cat.name }))}
                value={selectedCategoryId ? { value: selectedCategoryId, label: categories.find((c) => c.categoryId === selectedCategoryId)?.name || '' } : null}
                onChange={handleSelectChange(setSelectedCategoryId)}
                placeholder="All Categories"
                isClearable
              />
            </div>
          )}

          <div className="flex items-center gap-2">
            <div className="relative">
              <CalendarTodayIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" sx={{ fontSize: 14 }} />
              <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
                className="w-[140px] border border-gray-300 rounded-md py-2 pl-9 pr-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <span className="text-sm text-gray-400">to</span>
            <div className="relative">
              <CalendarTodayIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" sx={{ fontSize: 14 }} />
              <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
                className="w-[140px] border border-gray-300 rounded-md py-2 pl-9 pr-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
          </div>
        </div>

        {/* Filters Row 2 */}
        <div className="flex flex-wrap items-center gap-3">
          {userOptions.length > 0 && (
            <div className="w-full sm:w-[200px]">
              <ReactSelect options={userOptions}
                value={selectedUserId ? { value: selectedUserId, label: userOptions.find((u) => u.value === selectedUserId)?.label || '' } : null}
                onChange={handleSelectChange(setSelectedUserId)} placeholder="All Users" isClearable />
            </div>
          )}
          {projectOptions.length > 0 && (
            <div className="w-full sm:w-[200px]">
              <ReactSelect options={projectOptions}
                value={selectedProjectId ? { value: selectedProjectId, label: projectOptions.find((p) => p.value === selectedProjectId)?.label || '' } : null}
                onChange={(option: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
                  if (option && !Array.isArray(option) && 'value' in option) {
                    setSelectedProjectId(String(option.value));
                  } else {
                    setSelectedProjectId('');
                  }
                  setSelectedTaskId('');
                  setCurrentPage(1);
                }} placeholder="All Projects" isClearable />
            </div>
          )}
          {taskOptions.length > 0 && (
            <div className="w-full sm:w-[200px]">
              <ReactSelect options={taskOptions}
                value={selectedTaskId ? { value: selectedTaskId, label: taskOptions.find((t) => t.value === selectedTaskId)?.label || '' } : null}
                onChange={handleSelectChange(setSelectedTaskId)} placeholder="All Tasks" isClearable />
            </div>
          )}
          {hasActiveFilters && (
            <button type="button" onClick={clearFilters} className="text-sm text-indigo-600 hover:text-indigo-700 font-medium whitespace-nowrap">Clear filters</button>
          )}
        </div>
      </div>

      {/* Cards List */}
      {sortedUsage.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 overflow-hidden">
          <EmptyData description={hasActiveFilters || activeTab !== 'all'
            ? 'No equipment usage records found. Try adjusting your filters.'
            : 'No equipment usage history yet. Records will appear here when equipment is checked out.'} />
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {paginatedUsage.map((usage) => {
              const isActive = !usage.returnDate;
              const totalCost = usage.hoursUsed && usage.costPerHour ? usage.hoursUsed * usage.costPerHour : undefined;
              const displayUser = isActive
                ? usage.checkoutUser ? `${usage.checkoutUser.firstName} ${usage.checkoutUser.lastName}` : null
                : usage.returnUser ? `${usage.returnUser.firstName} ${usage.returnUser.lastName}`
                  : usage.checkoutUser ? `${usage.checkoutUser.firstName} ${usage.checkoutUser.lastName}` : null;

              return (
                <div key={usage.usageId} className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
                  {/* Row 1: Status + date */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {isActive ? (
                        <Badge size="sm" variant="warning">Checked Out</Badge>
                      ) : (
                        <Badge size="sm" variant="success">Returned</Badge>
                      )}
                      <span className="text-sm text-gray-500">
                        {usage.checkoutDate ? formatDateOnly(usage.checkoutDate) : '-'}
                        {usage.checkoutDate && (
                          <span className="ml-1 text-xs">
                            {new Date(usage.checkoutDate).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                          </span>
                        )}
                      </span>
                    </div>
                    {usage.condition && (
                      <Badge size="sm" variant={getConditionVariant(usage.condition as EquipmentCondition)}>
                        {CONDITION_LABELS[usage.condition as EquipmentCondition] || usage.condition}
                      </Badge>
                    )}
                  </div>

                  {/* Row 2: Equipment info + hours/cost */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900">{usage.asset?.name || 'Unknown Equipment'}</p>
                      {usage.asset?.assetCode && <p className="text-xs text-gray-400">{usage.asset.assetCode}</p>}
                    </div>
                    <div className="text-right ml-4">
                      {usage.hoursUsed != null && (
                        <p className="text-sm font-semibold text-gray-900">{usage.hoursUsed}h</p>
                      )}
                      {totalCost != null && (
                        <p className="text-xs font-medium text-red-600">{formatCurrency(totalCost)}</p>
                      )}
                    </div>
                  </div>

                  {/* Row 3: Details */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                    {displayUser && (
                      <span><span className="text-gray-400">By:</span> <span className="text-gray-700">{displayUser}</span></span>
                    )}
                    {usage.asset?.category?.name && (
                      <span><span className="text-gray-400">Category:</span> <span className="text-gray-700">{usage.asset.category.name}</span></span>
                    )}
                    {usage.task && (
                      <span><span className="text-gray-400">Task:</span> <span className="text-gray-700">{usage.task.name}</span></span>
                    )}
                    {usage.task?.project && (
                      <span><span className="text-gray-400">Project:</span> <span className="text-gray-700">{usage.task.project.name}</span></span>
                    )}
                    {usage.costPerHour != null && (
                      <span><span className="text-gray-400">Rate:</span> <span className="text-gray-700">{formatCurrency(usage.costPerHour)}/hr</span></span>
                    )}
                    {usage.returnDate && (
                      <span><span className="text-gray-400">Returned:</span> <span className="text-gray-700">{formatDateOnly(usage.returnDate)}</span></span>
                    )}
                  </div>

                  {usage.notes && (
                    <p className="text-xs text-gray-400 mt-2 italic truncate">{usage.notes}</p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 px-4 py-4 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing <span className="font-medium text-gray-900">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span> to{' '}
                <span className="font-medium text-gray-900">{Math.min(currentPage * ITEMS_PER_PAGE, sortedUsage.length)}</span> of{' '}
                <span className="font-medium text-gray-900">{sortedUsage.length}</span> records
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}
                  className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1">
                  <ChevronLeft className="h-4 w-4" /> Previous
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map((pageNum) => (
                  <button type="button" key={pageNum} onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium ${currentPage === pageNum ? 'bg-indigo-600 text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                    {pageNum}
                  </button>
                ))}
                {totalPages > 5 && (
                  <>
                    <span className="text-gray-500">...</span>
                    <button type="button" onClick={() => handlePageChange(totalPages)}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium ${currentPage === totalPages ? 'bg-indigo-600 text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                      {totalPages}
                    </button>
                  </>
                )}
                <button type="button" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}
                  className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1">
                  Next <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EquipmentTransactionHistoryTable;
